import type { ContentMatchInfo } from "@/stores/documents/repository";

/**
 * Build the Content chip tooltip from search match metadata. Returns:
 *   "Found on page 3"             — single-page hit (PDF/Word/etc.)
 *   "Found on page 3 (+4 more)"   — first match on p.3, 4 other pages match
 *   "Found on sheet Sheet1"       — Excel hit (sheet_name takes precedence)
 *   "Found on sheet Sheet1 (+4 more)"
 *   "Found in document text"      — fallback when info is missing (legacy
 *                                   callers that don't supply contentMatchInfo)
 *
 * Used by both `DocumentTableRow` (List view + /projects) and
 * `ServerTimelineNode` (Timeline view) so the chip text is consistent.
 *
 * `t` is the localized strings object from useLanguage(). We only access
 * the four keys we need; passing the full object keeps the helper free of
 * a translations import cycle.
 */
export interface ContentMatchTooltipStrings {
  contentMatchTooltip: string;
  contentMatchOnPage: string;
  contentMatchOnSheet: string;
  contentMatchMore: string;
}

export function formatContentMatchTooltip(
  info: ContentMatchInfo | null | undefined,
  strings: ContentMatchTooltipStrings
): string {
  if (!info) return strings.contentMatchTooltip;

  let base: string;
  if (info.sheetName) {
    base = strings.contentMatchOnSheet.replace("{sheet}", info.sheetName);
  } else if (info.firstMatchPage !== null) {
    base = strings.contentMatchOnPage.replace("{page}", String(info.firstMatchPage));
  } else {
    return strings.contentMatchTooltip;
  }

  if (info.matchCount > 1) {
    base += strings.contentMatchMore.replace("{count}", String(info.matchCount - 1));
  }
  return base;
}
